/**
 * @description PDF preview modal — displays invoice or estimate PDF in an iframe with Download + Print actions
 *
 * Key features:
 * - Fetches PDF blob from backend on open, renders in browser's native PDF viewer via iframe
 * - Download button saves PDF to disk with correct filename
 * - Print button triggers browser print dialog for the PDF
 * - Loading spinner while PDF generates, error state with retry
 * - Cleanup: revokes blob URL on close to prevent memory leaks
 * - Responsive: full-width on mobile, max-w-4xl on desktop
 * - Generic: works with any document type via fetchPdfBlob prop
 *
 * @dependencies
 * - @headlessui/react: Dialog + Transition
 * - invoiceService: getPdfBlob() API call (default)
 *
 * @notes
 * - PDF rendered by browser's native PDF viewer (no external library needed)
 * - Content-Disposition: inline from backend enables iframe rendering
 * - Follows enterprise modal pattern from .claude/context/patterns.md
 */

import React, { useState, useEffect, Fragment, useRef, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, Download, Printer, FileText, AlertCircle, Loader2 } from 'lucide-react';
import invoiceService from '../../services/api/invoiceService';

const PdfPreviewModal = ({ isOpen, onClose, invoiceNumber, title, documentNumber, fetchPdfBlob }) => {
  const docNumber = documentNumber || invoiceNumber;
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);

  const fetchPdf = useCallback(async () => {
    if (!docNumber) return;

    setLoading(true);
    setError(null);

    try {
      const blob = fetchPdfBlob
        ? await fetchPdfBlob(docNumber)
        : await invoiceService.getPdfBlob(docNumber);
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      setPdfUrl(url);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to generate PDF');
    } finally {
      setLoading(false);
    }
  }, [docNumber, fetchPdfBlob]);

  useEffect(() => {
    if (isOpen) {
      fetchPdf();
    }

    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, docNumber]);

  const handleClose = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setError(null);
    onClose();
  };

  const handleDownload = () => {
    if (!pdfUrl) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `${docNumber || 'document'}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.print();
    }
  };

  const displayTitle = title || `PDF — ${docNumber || ''}`;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        {/* Modal */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform rounded-2xl bg-white shadow-2xl transition-all flex flex-col" style={{ maxHeight: '90vh' }}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-gray-600" />
                    </div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      {displayTitle}
                    </Dialog.Title>
                  </div>
                  <button
                    onClick={handleClose}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 min-h-0 p-4">
                  {loading && (
                    <div className="flex flex-col items-center justify-center py-20">
                      <Loader2 className="w-8 h-8 text-brand animate-spin mb-3" />
                      <p className="text-sm text-gray-500">Generating PDF...</p>
                    </div>
                  )}

                  {error && (
                    <div className="flex flex-col items-center justify-center py-20">
                      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-3">
                        <AlertCircle className="w-6 h-6 text-red-500" />
                      </div>
                      <p className="text-sm font-medium text-gray-900 mb-1">Failed to generate PDF</p>
                      <p className="text-sm text-gray-500 mb-4">{error}</p>
                      <button
                        onClick={fetchPdf}
                        className="px-4 py-2 text-sm font-medium text-brand bg-brand/10 hover:bg-brand/20 rounded-lg transition-colors"
                      >
                        Try Again
                      </button>
                    </div>
                  )}

                  {pdfUrl && !loading && !error && (
                    <iframe
                      ref={iframeRef}
                      src={pdfUrl}
                      title={displayTitle}
                      className="w-full rounded-lg border border-gray-200"
                      style={{ height: 'calc(90vh - 160px)', minHeight: '400px' }}
                    />
                  )}
                </div>

                {/* Footer actions */}
                {pdfUrl && !loading && !error && (
                  <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleDownload}
                        className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition-colors shadow-sm active:scale-[0.98]"
                      >
                        <Download className="w-4 h-4" />
                        Download PDF
                      </button>
                      <button
                        onClick={handlePrint}
                        className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors"
                      >
                        <Printer className="w-4 h-4" />
                        Print
                      </button>
                    </div>
                    <button
                      onClick={handleClose}
                      className="px-4 py-2.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PdfPreviewModal;
